import { Component, forwardRef, Input, OnInit } from '@angular/core';
import {
  ControlValueAccessor,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ValidationErrors
} from '@angular/forms';
import { DatePickerQuestion } from '../../question-types/question-datepicker';
import { ForbiddenDateError } from '../../validators/date-comparison-validator';
import { LocalDate, IsoFields } from 'js-joda';
import { isNil } from 'lodash';

@Component({
  selector: 'ot-datepicker-question',
  templateUrl: './datepicker-question.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatepickerQuestionComponent),
      multi: true
    }
  ]
})
export class DatepickerQuestionComponent
  implements OnInit, ControlValueAccessor {
  @Input() questionFilter: DatePickerQuestion;
  @Input() parentForm: FormGroup;

  value: string;
  isDisabled: boolean;
  onChange = (_: any) => {};
  onTouch = () => {};

  constructor() {}
  ngOnInit() {
    this.value = this.questionFilter.value.toString();
  }

  isInvalidWithMessage(question: DatePickerQuestion): ValidationErrors {
    const control = this.parentForm.controls[question.key];
    if (!control.valid) {
      return control.errors;
    }

    return (
      this.dateAfterOtherValidator(question) ||
      this.dateRangeValidator(question) ||
      this.quarterExclusiveValidator(question)
    );
  }

  private dateAfterOtherValidator(
    question: DatePickerQuestion
  ): ForbiddenDateError {
    const valueDate = this.getLocalDateFromForm(question.key);
    const otherDate = this.getLocalDateFromForm(question.dateAfterOther);
    if (!valueDate || !otherDate) {
      return null;
    }
    if (isNil(question.dateAfterOther)) {
      return null;
    } else {
      if (question.key === 'endDate') {
        // question.dateAfterOther in this case is 'startDate'
        if (valueDate.isBefore(otherDate)) {
          question.isValid = false;
          return {
            forbiddenDate: {
              message: 'End date must be after start date.'
            }
          };
        } else {
          question.isValid = true;
          return null;
        }
      } else if (question.key === 'startDate') {
        // question.dateAfterOther in this case is 'endDate'
        if (valueDate.isAfter(otherDate)) {
          question.isValid = false;
          return {
            forbiddenDate: {
              message: 'End date must be after start date.'
            }
          };
        } else {
          question.isValid = true;
          return null;
        }
      } else {
        question.isValid = false;
        return {
          forbiddenDate: {
            message: 'Invalid Date key.'
          }
        };
      }
    }
  }

  private dateRangeValidator(question: DatePickerQuestion): ForbiddenDateError {
    const valueDate = this.getLocalDateFromForm(question.key);
    const otherDate = this.getLocalDateFromForm(question.dateRangeDate);
    if (!valueDate || !otherDate) {
      return null;
    }
    if (isNil(question.dateRangeDate) || isNil(question.dateRangeLimit)) {
      return null;
    } else {
      if (question.key === 'endDate') {
        // otherDate in this case is 'startDate'
        // check if otherDate is before valueDate.minusDays(dateRangeLimit)
        if (otherDate.isBefore(valueDate.minusDays(question.dateRangeLimit))) {
          question.isValid = false;
          return {
            forbiddenDate: {
              message: `Date Range cannot be more than ${
                question.dateRangeLimit
              } days.`
            }
          };
        } else {
          question.isValid = true;
          return null;
        }
      } else if (question.key === 'startDate') {
        // otherDate in this case is 'endDate'
        // check if otherDate is after valueDate.plusDays(dateRangeLimit)
        if (otherDate.isAfter(valueDate.plusDays(question.dateRangeLimit))) {
          question.isValid = false;
          return {
            forbiddenDate: {
              message: `Date Range cannot be more than ${
                question.dateRangeLimit
              } days.`
            }
          };
        } else {
          question.isValid = true;
          return null;
        }
      } else {
        question.isValid = false;
        return {
          forbiddenDate: { message: 'Invalid Date key.' }
        };
      }
    }
  }

  private quarterExclusiveValidator(
    question: DatePickerQuestion
  ): ForbiddenDateError {
    const valueDate = this.getLocalDateFromForm(question.key);
    const otherDate = this.getLocalDateFromForm(question.quarterExclusive);
    if (!valueDate || !otherDate) {
      return null;
    }
    if (isNil(question.dateRangeDate) || isNil(question.dateRangeLimit)) {
      return null;
    } else {
      // otherDate in this case is 'startDate'
      // check if otherDate is before valueDate.minusDays(dateRangeLimit)
      if (
        otherDate.get(IsoFields.QUARTER_OF_YEAR) !==
        valueDate.get(IsoFields.QUARTER_OF_YEAR)
      ) {
        question.isValid = false;
        return {
          forbiddenDate: {
            message: 'The dates cannot span over multiple quarters.'
          }
        };
      } else {
        question.isValid = true;
        return null;
      }
    }
  }

  onInput(value: string) {
    this.value = value;
    this.onTouch();
    this.onChange(this.value);
  }

  writeValue(value: any): void {
    if (value) {
      this.value = value;
    } else {
      this.value = null;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  getLocalDateFromForm(controlKey: string): LocalDate {
    try {
      const dateString = this.parentForm.controls[controlKey].value;
      return LocalDate.parse(dateString);
    } catch (error) {
      return null;
    }
  }
}
